
import { Component, Prop } from 'vue-property-decorator';
import Vue from 'vue';
import Groups from '../../group/Groups';
import GroupSurveyEditor from './EditModal/GroupSurveyEditor';
import TabSlot from '../../group/EditModal/TabSlot';
import GroupResultsModal from './GroupResultsModal';
import { emptyUUID } from '../../../util';
import {
  Group,
  GroupSurveyExtension,
  GroupSurveyExtensionUiClient,
  GroupUser,
  GroupUserExternal,
  ListItem,
  PreviewExternalUserMailRequest,
  PreviewUserMailRequest,
  SendExternalUserMailRequest,
  SendUserMailRequest,
} from '../../backend/backendapi';
import { handleError, initClient } from '../../../api';
import EmailModal, { SendEvent } from '../../util/EmailModal';
import Toasted from 'vue-toasted';
import GroupSurveyExtensionStore from '../../../stores/GroupSurveyExtensionStore';
import Spinner from '../../util/Spinner';
import ExternalUserSelectModalTab from '../../group/EditModal/ExternalUserSelectModal/ExternalUserSelectModalTab';
import ExternalUserSelectTab from './EditModal/ExternalUserSelectTab';
import GeneralAfter from './EditModal/GeneralAfter';
import PrintBadgeButton from './PrintBadgeButton';
import EditModal from '../../group/Editmodal.vue';
import EmailWithContactModal, { SendEvent as SendWithContactEvent } from './GroupsForSurveyEditModal/EmailWithContactModal.vue';
import { GroupSelector } from '../../group/util';

Vue.use(Toasted, { duration: 2000 });

@Component({
  methods: { emptyUUID },
  components: {
    EditModal,
    EmailModal,
    EmailWithContactModal,
    ExternalUserSelectTab,
    ExternalUserSelectModalTab,
    GeneralAfter,
    GroupResultsModal,
    GroupSurveyEditor,
    Groups,
    PrintBadgeButton,
    Spinner,
    TabSlot,
  },
})
export default class GroupsForSurveyEditModal extends Vue {
  @Prop({ required: true })
  readonly groups!: GroupSurveyExtension[];
  @Prop({ required: true })
  readonly availableSalutations!: string[];
  @Prop({ required: true })
  readonly allUsers!: ListItem[];
  @Prop({ default: 'group' })
  readonly groupSelector!: GroupSelector<GroupSurveyExtension>;
  @Prop({ default: () => (g: GroupSurveyExtension) => GroupSurveyExtension.fromJS(g) })
  readonly groupCopyFunction!: ((t: GroupSurveyExtension) => GroupSurveyExtension);
  @Prop({ default: null })
  readonly groupSaveFunction!: ((t: GroupSurveyExtension) => Promise<GroupSurveyExtension>) | null;
  @Prop({ default: false })
  readonly allowNoEmailForExternalUsers!: boolean;

  private readonly api = initClient(GroupSurveyExtensionUiClient);
  readonly store = new GroupSurveyExtensionStore();
  
  group: GroupSurveyExtension | null = null;

  GroupSurveyExtension = GroupSurveyExtension;

  sendingMailsToExternalUsers = false;

  async mounted(): Promise<void> {
    await this.store.reload();
  }

  public open(group: GroupSurveyExtension): void {
    this.group = group;
    (this.$refs.editmodal as EditModal).open(group);
  }

  public dismiss(): void {
    (this.$refs.editmodal as EditModal).dismiss();
    this.group = null;
  }

  async openMailUserModal(groupUser: GroupUser): Promise<void> {
    const groupSurveyExtensionId = this.getGroupSurveyExtensionForGroupId(groupUser.groupId).id;
    const preview = await this.api.previewUserMail(groupSurveyExtensionId, PreviewUserMailRequest.fromJS({ userId: groupUser.userId }));
    (this.$refs.userEmailModal as EmailModal).open(groupUser, preview.title, preview.content);
  }

  async openMailExternalUserModal(groupUserExternal: GroupUserExternal): Promise<void> {
    const groupSurveyExtension = this.getGroupSurveyExtensionForGroupId(groupUserExternal.groupId);
    const preview = await this.api.previewExternalUserMail(
        groupSurveyExtension.id,
        PreviewExternalUserMailRequest.fromJS({
          externalUserId: groupUserExternal.id,
          templateName: groupSurveyExtension.isAdvanceRegistration ? 'html_visitorManagement_openInstruction_externalUser' : undefined,
        }));
    (this.$refs.externalUserEmailModal as EmailWithContactModal).open(groupUserExternal, preview.title, preview.content);
  }

  async sendMailUser(e: SendEvent<GroupUser>): Promise<void> {
    try {
      const groupSurveyExtensionId = this.getGroupSurveyExtensionForGroupId(e.context.groupId).id;
      await this.api.sendUserMail(
          groupSurveyExtensionId,
          SendUserMailRequest.fromJS({ userId: e.context.userId, titleAndContent: { title: e.subject, content: e.html } }));
      (this.$refs.userEmailModal as EmailModal<GroupUser>).dismiss();
      this.$toasted.info(this.$t('general.emailSent').toString());
    } catch (error) {
      handleError(this.$toasted, this.$i18n, error);
    }
  }

  async sendMailExternalUser(e: SendWithContactEvent<GroupUserExternal>): Promise<void> {
    try {
      const groupSurveyExtensionId = this.getGroupSurveyExtensionForGroupId(e.context.groupId).id;
      await this.api.sendExternalUserMail(
          groupSurveyExtensionId,
          SendExternalUserMailRequest.fromJS({
            externalUserId: e.context.id,
            titleAndContent: { title: e.subject, content: e.html },
            recipientContactId: e.contact?.id,
          }));
      (this.$refs.externalUserEmailModal as EmailWithContactModal).dismiss();
      this.$toasted.info(this.$t('general.emailSent').toString());
    } catch (error) {
      handleError(this.$toasted, this.$i18n, error);
    }
  }

  async sendMailExternalUsers(groupUserExternals: GroupUserExternal[]): Promise<void> {
    this.sendingMailsToExternalUsers = true;
    try {
      const promises = groupUserExternals
          .filter(e => e.id && e.id !== emptyUUID())
          .map(e => {
            const groupSurveyExtensionId = this.getGroupSurveyExtensionForGroupId(e.groupId).id;
            return this.api.sendExternalUserMail(groupSurveyExtensionId, SendExternalUserMailRequest.fromJS({ externalUserId: e.id }));
          });
      await Promise.all(promises);
      this.$toasted.info(this.$t('general.emailSent').toString());
    } catch (error) {
      handleError(this.$toasted, this.$i18n, error);
    } finally {
      this.sendingMailsToExternalUsers = false;
    }
  }

  private getGroupSurveyExtensionForGroupId(groupId: string): GroupSurveyExtension {
    return this.groups.find(e => e.groupId === groupId)!;
  }
}
